import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AmenityReturn, ReservationWaitlistReturn } from '../clients/rust-api-client';
import { memberReservationService } from '../clients/ApiClients';
import { Badge } from '../Components/Catalyst/badge';
import { Button } from '../Components/Catalyst/button';
import { Checkbox, CheckboxField } from '../Components/Catalyst/checkbox';
import { Dialog, DialogActions, DialogBody, DialogTitle } from '../Components/Catalyst/dialog';
import { Field, Label } from '../Components/Catalyst/fieldset';
import { Subheading } from '../Components/Catalyst/heading';
import { Input } from '../Components/Catalyst/input';
import { Text } from '../Components/Catalyst/text';
import { ErrorAlert } from '../Components/Custom/ErrorAlert';
import { useImpersonating } from '../Hooks/useImpersonating';
import { formatLocalRange } from './reservationFormat';
import { feeLineAmount, offerPrice, OfferPrice, waitlistState, waitlistWindowText } from './reservationPhase3';
import {
  reservationErrorCode,
  reservationErrorMessage,
  ruleMessage,
  violationsFromError,
} from './ruleMessages';

interface Props {
  homeId: string;
}

const LIVE = ['waiting', 'offered'];
const nowSeconds = (): number => Math.floor(Date.now() / 1000);

/**
 * The household's waitlist (#640 phase 3): what it is waiting for, and any
 * time held for it. An offer holds its slot like a pending request for a
 * few hours; accepting books it through the ordinary path (every rule
 * judged again), passing hands it to the next household.
 */
const MyWaitlistSection: React.FC<Props> = ({ homeId }) => {
  const impersonating = useImpersonating();
  const [entries, setEntries] = useState<ReservationWaitlistReturn[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [accepting, setAccepting] = useState<ReservationWaitlistReturn | null>(null);
  const [now, setNow] = useState(nowSeconds());
  const [showClosed, setShowClosed] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    try {
      const { data } = await memberReservationService.listMyWaitlist({ homeId });
      setEntries(data.entries);
      setError(null);
    } catch (err) {
      setEntries([]);
      setError(reservationErrorMessage(err, "Couldn't load your waitlist."));
    } finally {
      setLoaded(true);
    }
  }, [homeId]);

  useEffect(() => {
    setLoaded(false);
    load();
  }, [load]);

  // The countdown on an offer; a minute is fine-grained enough.
  useEffect(() => {
    const t = window.setInterval(() => setNow(nowSeconds()), 30000);
    return () => window.clearInterval(t);
  }, []);

  const act = async (
    entry: ReservationWaitlistReturn,
    call: (entryId: string) => Promise<unknown>,
    fallback: string
  ): Promise<void> => {
    setBusy(entry.id);
    setError(null);
    try {
      await call(entry.id);
      await load();
    } catch (err) {
      setError(reservationErrorMessage(err, fallback));
    } finally {
      setBusy(null);
    }
  };

  if (!loaded) return null;
  const live = entries.filter((e) => LIVE.indexOf(e.status) !== -1);
  const closed = entries.filter((e) => LIVE.indexOf(e.status) === -1);
  if (live.length === 0 && closed.length === 0 && !error) return null;

  return (
    <section id="waitlist" className="mt-8">
      <Subheading>Waitlist</Subheading>
      <ErrorAlert className="mt-2">{error}</ErrorAlert>
      {live.length === 0 ? (
        <Text className="mt-2">You're not waiting for anything right now.</Text>
      ) : (
        <ul className="mt-3 space-y-3">
          {live.map((e) => {
            const state = waitlistState(e, now);
            return (
              <li
                key={e.id}
                className="rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-700 dark:bg-gray-800"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-semibold text-zinc-950 dark:text-white">{e.amenity_name}</span>
                  <Badge color={state.live ? 'green' : 'zinc'}>{state.label}</Badge>
                </div>
                <Text className="mt-1 text-sm">{waitlistWindowText(e)}</Text>
                <Text className="mt-2 text-sm">{state.detail}</Text>
                {state.live && <OfferPriceLines price={offerPrice(e)} />}
                <div className="mt-3 flex flex-wrap gap-3">
                  {state.live && (
                    <>
                      <Button color="sky" disabled={impersonating || busy === e.id} onClick={() => setAccepting(e)}>
                        Book this time
                      </Button>
                      <Button
                        outline
                        disabled={impersonating || busy === e.id}
                        onClick={() =>
                          act(
                            e,
                            (entryId) => memberReservationService.declineMyWaitlistOffer({ entryId }),
                            "Couldn't pass on that offer."
                          )
                        }
                      >
                        Pass
                      </Button>
                    </>
                  )}
                  {e.status === 'waiting' && (
                    <Button
                      outline
                      disabled={impersonating || busy === e.id}
                      onClick={() =>
                        act(
                          e,
                          (entryId) => memberReservationService.withdrawMyWaitlistEntry({ entryId }),
                          "Couldn't take you off the waitlist."
                        )
                      }
                    >
                      Leave waitlist
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {impersonating && live.length > 0 && (
        <Text className="mt-2 text-sm">
          This is a read-only view, so offers can't be taken or passed on here.
        </Text>
      )}
      {closed.length > 0 && (
        <div className="mt-3">
          <button
            type="button"
            className="text-sm text-zinc-600 hover:underline dark:text-zinc-400"
            onClick={() => setShowClosed((v) => !v)}
            aria-expanded={showClosed}
          >
            {showClosed ? 'Hide earlier entries' : `Earlier entries (${closed.length})`}
          </button>
          {showClosed && (
            <ul className="mt-2 space-y-1 text-sm text-zinc-600 dark:text-zinc-400">
              {closed.map((e) => (
                <li key={e.id}>
                  {e.amenity_name} · {waitlistWindowText(e)} — {waitlistState(e, now).label}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <AcceptOfferDialog
        homeId={homeId}
        entry={accepting}
        onClose={() => setAccepting(null)}
        onExpired={() => {
          setAccepting(null);
          load();
        }}
        onRepriced={async () => {
          // The price moved since the offer was loaded: reload the line and
          // show the dialog again on the fresh entry, so the household
          // agrees to the price it will actually pay.
          const fresh = await memberReservationService.listMyWaitlist({ homeId }).then(({ data }) => data.entries);
          setEntries(fresh);
          setAccepting(fresh.find((x) => x.id === accepting?.id && x.status === 'offered') ?? null);
        }}
      />
    </section>
  );
};

/** An offer's fee, its lines and any deposit — what booking it costs. */
const OfferPriceLines: React.FC<{ price: OfferPrice | null }> = ({ price }) => {
  if (!price) return null;
  return (
    <div className="mt-2 text-sm text-zinc-950 dark:text-white">
      <div className="flex justify-between gap-4">
        <span className="font-medium">Fee</span>
        <span className="tabular-nums">{price.fee}</span>
      </div>
      {price.lines.length > 0 && (
        <ul className="mt-1 space-y-0.5 text-zinc-600 dark:text-zinc-400">
          {price.lines.map((line, i) => (
            <li key={`${line.kind}-${i}`} className="flex justify-between gap-4">
              <span className="min-w-0">{line.label}</span>
              <span className="shrink-0 tabular-nums">{feeLineAmount(line)}</span>
            </li>
          ))}
        </ul>
      )}
      {price.deposit && <p className="mt-1 text-zinc-600 dark:text-zinc-400">{price.deposit}</p>}
    </div>
  );
};

interface AcceptProps {
  homeId: string;
  entry: ReservationWaitlistReturn | null;
  onClose: () => void;
  onExpired: () => void;
  onRepriced: () => void;
}

/**
 * Taking an offered time: the same agreement and vehicle the amenity asks
 * of any booking, then the booking itself, judged by every rule again.
 */
const AcceptOfferDialog: React.FC<AcceptProps> = ({ homeId, entry, onClose, onExpired, onRepriced }) => {
  const navigate = useNavigate();
  const [amenity, setAmenity] = useState<AmenityReturn | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [plate, setPlate] = useState('');
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Keyed on the entry's id, not the object: a re-priced offer is the same
  // offer reloaded, and must keep the "the price has changed" message.
  const entryId = entry?.id;
  const amenityId = entry?.amenity_id;
  useEffect(() => {
    setAmenity(null);
    setAgreed(false);
    setPlate('');
    setError(null);
    if (!entryId || !amenityId) return;
    memberReservationService
      .getMyAmenity({ homeId, amenityId })
      .then(({ data }) => setAmenity(data))
      .catch((err) => setError(reservationErrorMessage(err, "Couldn't load this amenity.")));
  }, [entryId, amenityId, homeId]);

  if (!entry) return null;
  const agreement = amenity?.requires_agreement ? amenity.current_agreement : null;
  const needsPlate = !!amenity?.requires_vehicle;

  const accept = async (): Promise<void> => {
    setWorking(true);
    setError(null);
    try {
      const { data } = await memberReservationService.acceptMyWaitlistOffer({
        entryId: entry.id,
        memberWaitlistAccept: {
          agreement_id: agreement?.id ?? null,
          vehicle_plate: needsPlate ? plate.trim() || null : null,
          // Hold the household to the price it was shown: a changed price
          // comes back as QUOTE_CHANGED rather than a different charge.
          quoted_fee: entry.offered_fee ?? null,
        },
      });
      navigate(`/reservations/${data.id}`);
    } catch (err) {
      const refused = violationsFromError(err);
      if (refused && refused.some((v) => v.code === 'QUOTE_CHANGED')) {
        setError('The price for this time has changed. Here it is again — check it before you book.');
        onRepriced();
      } else if (refused && refused.length) {
        setError(refused.map((v) => ruleMessage(v.code, amenity)).join(' '));
      } else {
        const message = reservationErrorMessage(err, "Couldn't book that time.");
        setError(message);
        const code = reservationErrorCode(err);
        if (code === 'WAITLIST_NO_OFFER' || code === 'WAITLIST_CLOSED') onExpired();
      }
    } finally {
      setWorking(false);
    }
  };

  return (
    <Dialog open={!!entry} onClose={onClose}>
      <DialogTitle>Book {entry.amenity_name}</DialogTitle>
      <DialogBody className="space-y-4">
        {entry.offered_start_local && entry.offered_end_local && (
          <Text>{formatLocalRange(entry.offered_start_local, entry.offered_end_local)}</Text>
        )}
        {agreement && (
          <div className="space-y-3">
            <div className="max-h-48 overflow-y-auto whitespace-pre-wrap rounded-lg bg-zinc-50 p-3 text-sm text-zinc-800 ring-1 ring-zinc-200 dark:bg-zinc-900 dark:text-zinc-200 dark:ring-zinc-700">
              <div className="mb-2 font-semibold">{agreement.title}</div>
              {agreement.body}
            </div>
            <CheckboxField>
              <Checkbox color="sky" checked={agreed} onChange={setAgreed} />
              <Label>I have read and agree to the {agreement.title}</Label>
            </CheckboxField>
          </div>
        )}
        {needsPlate && (
          <Field>
            <Label>Licence plate</Label>
            <Input value={plate} maxLength={16} onChange={(e) => setPlate(e.target.value.toUpperCase())} />
          </Field>
        )}
        {offerPrice(entry) ? (
          <OfferPriceLines price={offerPrice(entry)} />
        ) : (
          <Text className="text-sm">
            Any fee is worked out and charged as for any booking of {entry.amenity_name}.
          </Text>
        )}
        <ErrorAlert>{error}</ErrorAlert>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose}>
          Not now
        </Button>
        <Button
          color="sky"
          onClick={accept}
          disabled={working || !amenity || (!!agreement && !agreed) || (needsPlate && !plate.trim())}
        >
          {working ? 'Booking…' : 'Book it'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MyWaitlistSection;
